import React, { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { addDoc, collection, deleteDoc, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { FiAlertCircle, FiEdit2, FiExternalLink, FiImage, FiPlus, FiSearch, FiTrash2 } from 'react-icons/fi';
import { db } from '../../lib/firebase';
import { useSessionUser } from '../../lib/permissions';
import { formatDate } from '../../lib/format';
import { archiveDocuments, documentError, extensionOf, kindOf, sendFiles, thumbnailUrl } from '../../lib/documents';
import { BUTTON, CARD, EmptyState, Field, INPUT, Modal, Notice, PageHeader, Segmented, Select, SIZE, SkeletonRows, TextArea } from '../ui/kit';
import { UNITS } from '../patients/patientModel';
import AttachmentsField, { DocumentIcon, useDocumentsStatus } from '../documents/AttachmentsField';
import DocumentViewer from '../documents/DocumentViewer';
import { scheduleAccess } from '../schedule/scheduleModel';

// Images shared by the team (Firestore collection "iconographie": one entry per file, with a
// legend in "comment"). Files of the service's Dropbox are in `document`; former entries keep
// a Dropbox link in `link` until they are recovered.
const PAGE_SIZE = 60;
const KINDS = [
  { value: '', label: 'Tout' },
  { value: 'image', label: 'Photos' },
  { value: 'video', label: 'Vidéos' },
  { value: 'other', label: 'Documents' },
];
const kindGroup = (kind) => (kind === 'image' || kind === 'video' ? kind : 'other');
const monthOf = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return { key: '', label: 'Date inconnue' };
  const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
  return { key: date.toISOString().slice(0, 7), label: label.charAt(0).toUpperCase() + label.slice(1) };
};

// Thumbnail loaded when the tile comes near the screen.
const Thumbnail = ({ entry }) => {
  const box = useRef(null);
  const [url, setUrl] = useState('');
  const [near, setNear] = useState(false);
  useEffect(() => {
    const node = box.current;
    if (!node || near || entry.kind !== 'image' || !entry.document) return undefined;
    if (!('IntersectionObserver' in window)) {
      setNear(true);
      return undefined;
    }
    const observer = new IntersectionObserver((items) => items.some((item) => item.isIntersecting) && setNear(true), { rootMargin: '400px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [near, entry.kind, entry.document]);
  useEffect(() => {
    let cancelled = false;
    if (near && entry.document) thumbnailUrl(entry.document.id).then((value) => !cancelled && setUrl(value));
    return () => {
      cancelled = true;
    };
  }, [near, entry.document]);

  return (
    <span ref={box} className="absolute inset-0 flex items-center justify-center">
      {url ? (
        <img src={url} alt="" draggable={false} className="h-full w-full object-cover" />
      ) : (
        <span className="flex flex-col items-center gap-1.5 text-black/35">
          <DocumentIcon name={entry.name} mimeType={entry.mimeType} size={26} />
          <span className="text-[11px] font-semibold uppercase tracking-wide">{extensionOf(entry.name) || 'fichier'}</span>
        </span>
      )}
    </span>
  );
};

const Tile = memo(({ entry, editable, deletable, onOpen, onEdit, onDelete }) => {
  const tile = 'relative block aspect-square w-full overflow-hidden rounded-2xl bg-black/[0.04] transition hover:opacity-90';
  return (
    <li className="min-w-0">
      {entry.document ? (
        <button type="button" onClick={() => onOpen(entry)} aria-label={`Ouvrir ${entry.name}`} className={`${tile} cursor-pointer border-0 p-0`}>
          <Thumbnail entry={entry} />
        </button>
      ) : (
        <a href={entry.link} target="_blank" rel="noopener noreferrer" aria-label={`Ouvrir ${entry.name} (ancien lien Dropbox)`} className={`${tile} no-underline`}>
          <Thumbnail entry={entry} />
          <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[11px] font-medium text-black/60">
            <FiExternalLink size={11} aria-hidden="true" />
            Ancien lien
          </span>
        </a>
      )}
      <div className="mt-2 flex items-start gap-1">
        <div className="min-w-0 flex-1">
          <p className="m-0 line-clamp-2 break-words text-[13px] font-medium leading-snug text-ink">{entry.legend || entry.name}</p>
          <p className="mb-0 mt-0.5 truncate text-[12px] text-black/40">{[entry.dateText, entry.item.service].filter(Boolean).join(' · ')}</p>
        </div>
        {editable && (
          <button type="button" onClick={() => onEdit(entry.item)} aria-label={`Modifier ${entry.name}`} title="Modifier" className={`${BUTTON.ghost} h-8 w-8 flex-none p-0 text-black/45`}>
            <FiEdit2 size={14} aria-hidden="true" />
          </button>
        )}
        {deletable && (
          <button type="button" onClick={() => onDelete(entry.item)} aria-label={`Supprimer ${entry.name}`} title="Supprimer" className={`${BUTTON.ghost} h-8 w-8 flex-none p-0 text-red-500 hover:bg-red-50 hover:text-red-600`}>
            <FiTrash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </li>
  );
});

const IconographiePage = () => {
  const user = useSessionUser();
  const access = scheduleAccess(user, 'iconographie');
  const documentsStatus = useDocumentsStatus();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading');
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [unit, setUnit] = useState(null); // null: the user's unit when present
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [dialog, setDialog] = useState(null); // { mode: 'add' | 'edit' | 'delete', item? }
  const [form, setForm] = useState({ legend: '', unit: '', pending: [], links: [] });
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(null);
  const [dialogError, setDialogError] = useState('');
  const [notice, setNotice] = useState('');
  const [viewing, setViewing] = useState(null); // index in `viewable`

  useEffect(
    () =>
      onSnapshot(
        collection(db, 'iconographie'),
        (snapshot) => {
          setItems(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
          setStatus('ready');
        },
        () => setStatus('error')
      ),
    []
  );

  // Date, kind and search text computed once per change of the data.
  const prepared = useMemo(
    () =>
      items
        .map((item) => {
          const document = item.document && item.document.id ? item.document : null;
          const name = item.name || (document && document.name) || 'Fichier';
          const added = item.createdAt || item.timestamp || '';
          const legend = String(item.comment || '').trim();
          const entryKind = kindOf(name, document && document.mimeType);
          const month = monthOf(added);
          return {
            item,
            document,
            link: document ? '' : item.link || '',
            name,
            mimeType: document ? document.mimeType : '',
            legend,
            added,
            dateText: formatDate(added),
            kind: entryKind,
            group: kindGroup(entryKind),
            month,
            search: [name, legend, item.service, formatDate(added), month.label].join(' ').toLowerCase(),
          };
        })
        .sort((a, b) => String(b.added).localeCompare(String(a.added))),
    [items]
  );

  const unitsInData = useMemo(() => [...new Set(items.map((item) => item.service).filter(Boolean))].sort(), [items]);
  const defaultUnit = unitsInData.find((name) => access.myUnits.includes(name.toLowerCase())) || '';
  const activeUnit = unit === null ? defaultUnit : unit;

  const query = useDeferredValue(search.trim().toLowerCase());
  const matching = useMemo(
    () =>
      prepared.filter(
        (entry) =>
          // Former entries have no unit: they concern every unit.
          (!activeUnit || !entry.item.service || entry.item.service === activeUnit) && (!query || query.split(/\s+/).every((word) => entry.search.includes(word)))
      ),
    [prepared, activeUnit, query]
  );
  const counts = useMemo(() => {
    const result = { '': matching.length, image: 0, video: 0, other: 0 };
    matching.forEach((entry) => {
      result[entry.group] += 1;
    });
    return result;
  }, [matching]);
  const shown = useMemo(() => (kind ? matching.filter((entry) => entry.group === kind) : matching), [matching, kind]);
  const visible = shown.slice(0, limit);
  // Months of the tiles drawn, in order.
  const months = useMemo(() => {
    const result = [];
    visible.forEach((entry) => {
      const last = result[result.length - 1];
      if (last && last.key === entry.month.key) last.entries.push(entry);
      else result.push({ key: entry.month.key, label: entry.month.label, entries: [entry] });
    });
    return result;
  }, [visible]);
  // The viewer browses the files of the service's Dropbox in the order of the gallery.
  const viewable = useMemo(() => visible.filter((entry) => entry.document), [visible]);

  useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [kind, activeUnit, query]);

  const unitChoices = access.isAdmin ? UNITS : access.myUnits.length > 1 ? UNITS.filter((name) => access.myUnits.includes(name.toLowerCase())) : [];

  const openAdd = () => {
    setForm({ legend: '', unit: unitChoices.length ? '' : user.service || '', pending: [], links: [] });
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'add' });
  };
  const openEdit = useCallback((item) => {
    setForm({ legend: String(item.comment || ''), unit: item.service || '', pending: [], links: [] });
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'edit', item });
  }, []);
  const openDelete = useCallback((item) => {
    setDialogError('');
    setNotice('');
    setDialog({ mode: 'delete', item });
  }, []);
  const openViewer = useCallback((entry) => setViewing(viewable.findIndex((candidate) => candidate === entry)), [viewable]);
  const close = () => !busy && setDialog(null);

  const add = async () => {
    if (unitChoices.length && !form.unit) {
      setDialogError('Choisissez l’unité.');
      return;
    }
    if (!form.pending.length && !form.links.length) {
      setDialogError('Ajoutez au moins un fichier.');
      return;
    }
    setBusy(true);
    setDialogError('');
    const entryUnit = form.unit || user.service || '';
    const now = new Date().toISOString();
    const base = { comment: form.legend.trim(), service: entryUnit, createdBy: user.name || '', createdAt: now, timestamp: now };
    let sent = [];
    let failure = null;
    if (form.pending.length) {
      try {
        sent = await sendFiles(form.pending, { section: 'iconographie', unit: entryUnit }, setSending);
      } catch (error) {
        sent = error.sent || [];
        failure = error;
      } finally {
        setSending(null);
      }
      setForm((current) => ({ ...current, pending: current.pending.slice(sent.length) }));
    }
    try {
      for (const document of sent) {
        // eslint-disable-next-line no-await-in-loop
        await addDoc(collection(db, 'iconographie'), { ...base, name: document.name, document: { id: document.id, name: document.name, mimeType: document.mimeType || '' } });
      }
      // Files chosen in a personal Dropbox while the service's Dropbox is not linked.
      for (const item of failure ? [] : form.links) {
        // eslint-disable-next-line no-await-in-loop
        await addDoc(collection(db, 'iconographie'), { ...base, name: item.name, link: item.link });
      }
    } catch (error) {
      failure = failure || error;
    }
    const count = sent.length + (failure ? 0 : form.links.length);
    if (failure) {
      setDialogError(await documentError(failure, 'Un fichier n’a pas pu être enregistré. Réessayez.'));
    } else {
      setDialog(null);
      setNotice(`${count} fichier${count > 1 ? 's' : ''} ajouté${count > 1 ? 's' : ''} à l’iconographie.`);
      setKind('');
    }
    setBusy(false);
  };

  const saveEdit = async () => {
    setBusy(true);
    setDialogError('');
    try {
      await updateDoc(doc(db, 'iconographie', dialog.item.id), {
        comment: form.legend.trim(),
        ...(form.unit ? { service: form.unit } : {}),
        updatedBy: user.name || '',
        updatedAt: new Date().toISOString(),
      });
      setDialog(null);
      setNotice('Légende enregistrée.');
    } catch (error) {
      setDialogError('La modification n’a pas pu être enregistrée.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setDialogError('');
    try {
      const stored = dialog.item.document && dialog.item.document.id ? [dialog.item.document] : [];
      if ((await archiveDocuments(stored)).length) throw new Error('archive');
      await deleteDoc(doc(db, 'iconographie', dialog.item.id));
      setDialog(null);
      setNotice('Fichier retiré de l’iconographie.');
    } catch (error) {
      setDialogError(error.message === 'archive' ? 'Le fichier n’a pas pu être retiré du Dropbox du service. Réessayez.' : 'Le fichier n’a pas pu être retiré.');
    } finally {
      setBusy(false);
    }
  };

  const kindChip = ({ value, label }) => {
    if (value && !counts[value]) return null;
    const active = kind === value;
    return (
      <button
        key={value || 'all'}
        type="button"
        aria-pressed={active}
        onClick={() => setKind(value)}
        className={`inline-flex h-9 flex-none cursor-pointer items-center gap-2 whitespace-nowrap rounded-full border-0 px-[14px] text-[13.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600/40 ${
          active ? 'bg-ink text-white' : 'bg-white text-black/65 shadow-[0_1px_2px_rgba(0,0,0,0.06)] hover:text-ink'
        }`}
      >
        {label}
        <span className={`tabular-nums ${active ? 'text-white/60' : 'text-black/35'}`}>{counts[value]}</span>
      </button>
    );
  };

  const current = viewing !== null ? viewable[viewing] : null;
  const unitField = unitChoices.length > 0 && (
    <Field id="icono-unit" label="Unité" required={dialog && dialog.mode === 'add'}>
      <Select
        id="icono-unit"
        value={form.unit}
        onChange={(e) => setForm({ ...form, unit: e.target.value })}
        placeholder="Choisir l’unité…"
        options={form.unit && !unitChoices.includes(form.unit) ? [form.unit, ...unitChoices] : unitChoices}
      />
    </Field>
  );
  const more = Math.min(PAGE_SIZE, shown.length - limit);

  return (
    <main className="mx-auto max-w-6xl px-[20px] pb-16 pt-8 sm:px-8 lg:px-10 lg:pt-10">
      <PageHeader
        eyebrow="Équipe"
        title="Iconographie"
        subtitle={status === 'ready' ? `${activeUnit ? `Unité ${activeUnit}` : 'Toutes les unités'} · ${counts['']} fichier${counts[''] > 1 ? 's' : ''}` : 'Chargement de l’iconographie…'}
        actions={
          access.canAdd && (
            <button type="button" onClick={openAdd} className={`${BUTTON.primary} ${SIZE.md}`}>
              <FiPlus size={16} aria-hidden="true" />
              Ajouter des images
            </button>
          )
        }
      />

      {notice && (
        <div className="mt-6">
          <Notice tone="green">{notice}</Notice>
        </div>
      )}

      <div className="mt-8 flex flex-col gap-[12px]">
        <div className="relative">
          <FiSearch size={17} aria-hidden="true" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2 text-black/35" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Légende, nom du fichier, mois…"
            aria-label="Rechercher dans l’iconographie"
            className={`${INPUT} h-11 pl-[42px]`}
          />
        </div>
        <div className="flex flex-col gap-[12px] lg:flex-row lg:items-center lg:justify-between">
          <div className="-mx-[20px] flex gap-2 overflow-x-auto px-[20px] pb-1 [scrollbar-width:none] sm:mx-0 sm:flex-wrap sm:px-0 [&::-webkit-scrollbar]:hidden">{KINDS.map(kindChip)}</div>
          {unitsInData.length > 1 && (
            <Segmented value={activeUnit} onChange={setUnit} allowEmpty={false} options={[{ value: '', label: 'Toutes les unités' }, ...unitsInData.map((name) => ({ value: name, label: name }))]} />
          )}
        </div>
      </div>

      <div className="mt-[20px]">
        {status === 'loading' ? (
          <div className={`${CARD} p-6`}>
            <SkeletonRows rows={4} />
          </div>
        ) : status === 'error' ? (
          <div className={CARD}>
            <EmptyState icon={FiAlertCircle} title="Iconographie indisponible">
              Elle n’a pas pu être chargée. Vérifiez la connexion, puis rechargez la page.
            </EmptyState>
          </div>
        ) : shown.length === 0 ? (
          <div className={CARD}>
            <EmptyState icon={FiImage} title="Aucun fichier">
              {query ? 'Aucun fichier ne correspond à cette recherche.' : 'Les images partagées par l’équipe apparaîtront ici.'}
            </EmptyState>
          </div>
        ) : (
          <div className="flex flex-col gap-8">
            {months.map((month) => (
              <section key={month.key || 'unknown'}>
                <h2 className="mb-[12px] mt-0 px-1 text-[13px] font-semibold uppercase tracking-[0.06em] text-black/40">{month.label}</h2>
                <ul className="m-0 grid list-none grid-cols-2 gap-x-[14px] gap-y-[18px] p-0 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                  {month.entries.map((entry) => (
                    <Tile
                      key={entry.item.id}
                      entry={entry}
                      editable={access.canEdit(entry.item)}
                      deletable={access.canDelete(entry.item)}
                      onOpen={openViewer}
                      onEdit={openEdit}
                      onDelete={openDelete}
                    />
                  ))}
                </ul>
              </section>
            ))}
            {limit < shown.length && (
              <div className="flex flex-col items-center gap-1 text-center">
                <button type="button" onClick={() => setLimit((count) => count + PAGE_SIZE)} className={`${BUTTON.ghost} ${SIZE.md}`}>
                  Afficher {more} fichier{more > 1 ? 's' : ''} de plus
                </button>
                <span className="text-[12.5px] text-black/40">
                  {limit} sur {shown.length} affichés
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <Modal
        open={Boolean(dialog && dialog.mode === 'add')}
        title="Ajouter des images"
        onClose={close}
        size="lg"
        footer={
          <>
            <button type="button" onClick={close} disabled={busy} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={add} disabled={busy} className={`${BUTTON.primary} ${SIZE.md}`}>
              {sending ? `Envoi ${sending.index + 1}/${form.pending.length}…` : busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-[18px]">
          <Field id="icono-files" label="Fichiers" required>
            <AttachmentsField
              id="icono-files"
              status={documentsStatus}
              links={form.links}
              onLinksChange={busy ? null : (links) => setForm((current) => ({ ...current, links }))}
              pending={form.pending}
              onPendingChange={(pending) => {
                setForm((current) => ({ ...current, pending }));
                setDialogError('');
              }}
              sending={sending}
            />
          </Field>
          <Field id="icono-legend" label="Légende" hint="Facultatif ; la même légende est donnée à chaque fichier ajouté.">
            <TextArea id="icono-legend" value={form.legend} onChange={(e) => setForm({ ...form, legend: e.target.value })} placeholder="ex. Cas de la semaine : sternotomie" />
          </Field>
          {unitField}
          {dialogError && <Notice tone="red">{dialogError}</Notice>}
        </div>
      </Modal>

      <Modal
        open={Boolean(dialog && dialog.mode === 'edit')}
        title="Modifier la légende"
        onClose={close}
        footer={
          <>
            <button type="button" onClick={close} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={saveEdit} disabled={busy} className={`${BUTTON.primary} ${SIZE.md}`}>
              {busy ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-[18px]">
          {dialog && dialog.item && <p className="m-0 break-words text-[14px] font-medium text-ink">{dialog.item.name}</p>}
          <Field id="icono-edit-legend" label="Légende">
            <TextArea id="icono-edit-legend" value={form.legend} onChange={(e) => setForm({ ...form, legend: e.target.value })} />
          </Field>
          {unitField}
          {dialogError && <Notice tone="red">{dialogError}</Notice>}
        </div>
      </Modal>

      <Modal
        open={Boolean(dialog && dialog.mode === 'delete')}
        title="Retirer le fichier"
        onClose={close}
        footer={
          <>
            <button type="button" onClick={close} className={`${BUTTON.secondary} ${SIZE.md}`}>
              Annuler
            </button>
            <button type="button" onClick={remove} disabled={busy} className={`${BUTTON.dangerSolid} ${SIZE.md}`}>
              {busy ? 'Suppression…' : 'Retirer'}
            </button>
          </>
        }
      >
        <p className="m-0 text-[14.5px] leading-relaxed text-black/55">
          <strong className="text-ink">{dialog && dialog.item && dialog.item.name}</strong> sera retiré de l’iconographie.{' '}
          {dialog && dialog.item && dialog.item.document ? 'Il reste conservé dans les archives du Dropbox du service.' : 'Le fichier reste dans Dropbox.'}
        </p>
        {dialogError && (
          <div className="mt-[12px]">
            <Notice tone="red">{dialogError}</Notice>
          </div>
        )}
      </Modal>

      {current && (
        <DocumentViewer
          key={current.document.id}
          item={current.document}
          onClose={() => setViewing(null)}
          canReplace={access.canEdit(current.item)}
          canDownload={access.canDownload}
          onPrevious={viewing > 0 ? () => setViewing(viewing - 1) : null}
          onNext={viewing < viewable.length - 1 ? () => setViewing(viewing + 1) : null}
        />
      )}
    </main>
  );
};

export default IconographiePage;
